import { toRaw } from 'vue';
import cloneDeep from 'lodash/cloneDeep';

/**
 * Recursively cleans Vue reactive properties from an object.
 * This function removes Vue internal properties like _isVue, __v_isRef, etc.
 * and returns a plain JavaScript object.
 *
 * @param {*} obj - The object to clean (can be any type)
 * @returns {*} - A cleaned plain object without Vue reactive properties
 */
export const cleanReactiveObject = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => cleanReactiveObject(item));
  }
  
  // Get raw object if it's a reactive proxy
  let raw = toRaw(obj);
  if (raw === obj) {
    raw = obj;
  }
  
  // Use cloneDeep to deeply clone, which will remove reactive properties
  const cloned = cloneDeep(raw);
  
  // Remove any remaining Vue internal properties recursively
  const cleanObject = (target) => {
    if (!target || typeof target !== 'object') {
      return target;
    }
    
    if (Array.isArray(target)) {
      return target.map(item => cleanObject(item));
    }
    
    const cleaned = {};
    for (const key in target) {
      // Skip Vue internal properties
      if (key === '_isVue' || 
          key === '__v_isRef' || 
          key === '__v_isReactive' || 
          key === '__v_isReadonly' ||
          key.startsWith('__v_')) {
        continue;
      }
      
      const value = target[key];
      if (value && typeof value === 'object') {
        cleaned[key] = cleanObject(value);
      } else {
        cleaned[key] = value;
      }
    }
    
    return cleaned;
  };
  
  return cleanObject(cloned);
};

